import React from "react";
import { motion } from "framer-motion";
import PageHeader from "../components/PageHeader";
import { Reveal, Stagger, staggerItem } from "../components/Motion";
import { Target, Compass, Heart, Sparkles, Users, ShieldCheck } from "lucide-react";

const values = [
  { icon: Heart, title: "Learner-first", body: "Every decision begins with the question: does this serve our students' growth and outcomes?" },
  { icon: ShieldCheck, title: "Integrity", body: "We hold ourselves to high standards of honesty, transparency and academic rigor." },
  { icon: Users, title: "Community", body: "We believe a strong community of peers, mentors and alumni is essential to lifelong success." },
  { icon: Sparkles, title: "Innovation", body: "We continually adapt our programs and teaching to keep pace with industry change." },
];

const About = () => {
  return (
    <div data-testid="about-page">
      <PageHeader
        kicker="About Heritage"
        title="A new college, built for the way Canada works."
        subtitle="Heritage Career College of Business and Technology is an Ontario-based career college reimagining post-secondary education for working learners, newcomers, and ambitious professionals across Canada."
        image="https://images.unsplash.com/photo-1774600787713-bc405e935202?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB1bml2ZXJzaXR5JTIwYnVpbGRpbmclMjBleHRlcmlvcnxlbnwwfHx8fDE3NzgwMTQyNjl8MA&ixlib=rb-4.1.0&q=85"
      />

      {/* Vision / Mission */}
      <section className="hccbt-container section">
        <div className="grid lg:grid-cols-2 gap-10">
          <Reveal>
          <motion.div whileHover={{ y: -4 }} className="card-soft card-lift p-10" data-testid="vision-card">
            <div className="w-12 h-12 rounded-full bg-[#87CEEB]/25 flex items-center justify-center text-[#0F172A]">
              <Target size={22} />
            </div>
            <span className="overline mt-6 block">Our Vision</span>
            <h2 className="font-display text-3xl mt-3 leading-tight text-[#0F172A]">
              To be Canada's most trusted career college — known for graduates who lead with skill, integrity, and care.
            </h2>
          </motion.div>
          </Reveal>

          <Reveal delay={0.1}>
          <motion.div whileHover={{ y: -4 }} className="card-soft card-lift p-10" data-testid="mission-card">
            <div className="w-12 h-12 rounded-full bg-[#87CEEB]/25 flex items-center justify-center text-[#0F172A]">
              <Compass size={22} />
            </div>
            <span className="overline mt-6 block">Our Mission</span>
            <h2 className="font-display text-3xl mt-3 leading-tight text-[#0F172A]">
              To deliver accessible, career-focused programs that prepare every learner to thrive in Canada's modern economy.
            </h2>
            <p className="text-[#475569] mt-5 leading-relaxed">
              We blend rigorous academics with hands-on experience, mentorship from
              industry-experienced faculty, and a learning environment designed to
              support students from orientation to graduation — and beyond.
            </p>
          </motion.div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white border-y border-[#E2E8F0]">
        <div className="hccbt-container section">
          <div className="max-w-2xl">
            <span className="overline">Our Values</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight text-[#0F172A]">
              Four principles that guide every classroom, conversation and decision.
            </h2>
          </div>
          <Stagger className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <motion.div
                key={v.title}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                className="card-soft card-lift p-6"
                data-testid={`value-${v.title.toLowerCase()}`}
              >
                <motion.div
                  className="w-11 h-11 rounded-full bg-[#87CEEB]/20 flex items-center justify-center text-[#0F172A]"
                  whileHover={{ rotate: 8, scale: 1.08 }}
                  transition={{ type: "spring", stiffness: 240, damping: 14 }}
                >
                  <v.icon size={20} />
                </motion.div>
                <h3 className="font-display text-xl mt-5 text-[#0F172A]">{v.title}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Story */}
      <section className="hccbt-container section">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="overline">Our Story</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight text-[#0F172A]">
              Founded for the next generation of Canadian professionals.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[#475569] leading-relaxed">
            <p>
              Heritage Career College was founded by educators and industry
              leaders who saw a clear gap: career-focused students wanted
              programs that were modern, practical, mentor-rich and grounded in
              real Canadian employment opportunities.
            </p>
            <p>
              We are launching in Ontario with a single, deeply-resourced
              program — the Diploma in Business Management — and a commitment to
              do that program exceptionally well. From this strong foundation,
              we plan to thoughtfully expand into IT, digital marketing,
              accounting, supply chain and healthcare administration.
            </p>
            <p>
              Heritage is being built for every learner — newcomers building a
              future in Canada, working professionals returning to study, and
              recent graduates looking for a faster, more practical path into
              industry.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
